import React, { useRef, useEffect } from "react";
import { Box, Text } from "@chakra-ui/react";
import { Section } from "components/section";
import { TextTitle, type TextTitleProps } from "ui/text";
import { BookDemoButton } from "components/event-tracking";
import * as Sentry from "@sentry/nextjs";
import { useAnimate } from "framer-motion";

const sourceMp4 = "https://cdn.sanity.io/files/pwmfmi47/production/991b79696e6a90e465084408686f43cadceac9ba.mov";
const sourceWebm = "https://cdn.sanity.io/files/pwmfmi47/production/095106805932ea736e3bfc2795b7fe0c5b5197ab.webm";

type AgentsHeroProps = {
  eyebrow: string;
  title: string;
  description: string;
};

const sharedProps: Pick<
  TextTitleProps,
  "align" | "mobileAlign" | "size" | "titleAs" | "descriptionAs" | "descriptionMaxW" | "actions"
> = {
  align: "center",
  mobileAlign: "center",
  size: "lg",
  titleAs: "h1",
  descriptionAs: "h2",
  descriptionMaxW: "700px",
};

export function AgentsHero({ eyebrow, title, description }: AgentsHeroProps) {
  return (
    <Section
      center
      themeBgColor="white"
      my={0}
      pt={{ base: "48px", md: "sectionSpacingY.md" }}
      pb={{ base: "big", lg: "hugest" }}
      pos="relative"
    >
      <Box display={{ base: "none", lg: "block" }}>
        <AgentsHeroDesktop eyebrow={eyebrow} title={title} description={description} />
      </Box>
      <Box display={{ base: "block", lg: "none" }}>
        <AgentsHeroMobile eyebrow={eyebrow} title={title} description={description} />
      </Box>
    </Section>
  );
}

export function AgentsHeroDesktop({ eyebrow, title, description }: AgentsHeroProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const scope = useAgentsHeroAnimation();

  // Set playback rate to 1.6 when video is ready
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const setPlaybackRate = () => {
        video.playbackRate = 1.6;
      };

      if (video.readyState >= 2) {
        setPlaybackRate();
      } else {
        video.addEventListener("loadeddata", setPlaybackRate);
      }

      video.addEventListener("canplay", setPlaybackRate);

      return () => {
        video.removeEventListener("loadeddata", setPlaybackRate);
        video.removeEventListener("canplay", setPlaybackRate);
      };
    }
  }, []);

  return (
    <Box ref={scope}>
      <Box
        pos="absolute"
        top={0}
        transform="translateX(-50%) translateY(-16%)"
        left="50%"
        w={{ base: "100%", xl: "90%", "2xl": "85%" }}
        zIndex={-1}
      >
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          preload="metadata"
          style={{
            width: "100%",
            maxWidth: "1700px",
            objectFit: "cover",
            margin: "0 auto",
          }}
        >
          <source src={sourceMp4} type="video/mp4;codecs=hvc1" />
          {sourceWebm && <source src={sourceWebm} type="video/webm" />}
        </video>
      </Box>

      <Box
        id="agents-hero-content-wrapper"
        pos="relative"
        bg="radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 95%)"
      >
        <Box id="agents-hero-content-inner">
          <Text id="agents-hero-eyebrow" textStyle="caption1" fontWeight="semibold" textAlign="center" mb="smaller">
            {eyebrow}
          </Text>
          <TextTitle
            {...sharedProps}
            title={title}
            titleAs="h1"
            description={
              <Box id="agents-hero-description">
                <Text as="h2">{description}</Text>
              </Box>
            }
            descriptionAs="div"
            descriptionMaxW="xl"
            actions={
              <Box id="agents-hero-cta">
                <BookDemoButton useAgentsRouter />
              </Box>
            }
          />
        </Box>
      </Box>
    </Box>
  );
}

function useAgentsHeroAnimation() {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        const viewportWidth = typeof window === "undefined" ? 0 : window.innerWidth;
        const preferredValue = viewportWidth * 0.06; // 6vw
        const clampedValue = Math.max(64, Math.min(170, preferredValue));

        await animate([
          [
            "#agents-hero-content-inner",
            { opacity: [0, 1], y: [32, 0], scale: [0.99, 1] },
            {
              at: 0.8,
              duration: 1,
              ease: [0.33, 1, 0.68, 1],
            },
          ],
          [
            "#agents-hero-content-wrapper",
            { y: [clampedValue, 0] },
            {
              at: 3.5,
              duration: 0.8,
              ease: [0.65, 0, 0.35, 1],
            },
          ],
          [
            "#agents-hero-cta",
            { y: [-80, 0] },
            {
              at: 3.5,
              duration: 0.6,
              ease: [0.65, 0, 0.35, 1],
            },
          ],
          [
            "#agents-hero-eyebrow",
            { opacity: [0, 1], y: [4, 0], scale: [0.99, 1] },
            {
              at: 3.8,
              duration: 1,
              ease: [0.33, 1, 0.68, 1],
            },
          ],
          [
            "#agents-hero-description",
            { opacity: [0, 1], y: [8, 0], scale: [0.99, 1] },
            {
              at: 3.8,
              duration: 0.6,
              ease: [0.33, 1, 0.68, 1],
            },
          ],
        ]);
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [animate]);

  return scope;
}

export function AgentsHeroMobile({ eyebrow, title, description }: AgentsHeroProps) {
  return (
    <TextTitle
      {...sharedProps}
      chip={{ text: eyebrow }}
      title={title}
      titleAs="h1"
      description={description}
      descriptionAs="h2"
      actions={<BookDemoButton useAgentsRouter />}
    />
  );
}
